import { useEffect, useMemo, useRef, memo } from 'react';
import isEqual from 'lodash/isEqual';
import { useQueryParams } from '@atlassian/jira-software-router-utils/src/services/query-params/index.tsx';
import {
	QUERY_PARAM_ASSIGNEE,
	QUERY_PARAM_ISSUE_PARENT,
	QUERY_PARAM_VERSION,
	QUERY_PARAM_ISSUE_TYPE,
	QUERY_PARAM_LABEL,
	QUERY_PARAM_TEXT,
	QUERY_PARAM_CUSTOM_FILTER,
	QUERY_PARAM_QUICK_FILTER,
	QUERY_PARAM_SEARCH,
	QUERY_PARAM_JQL,
} from '../../common/constants.tsx';
import { getFiltersFromQuery, getQueryFromFilters } from '../../common/utils/parsing/index.tsx';
import { useFilters } from '../filters/main.tsx';

export const SyncWithRouting = memo<{
	isVersionsFeatureEnabled: boolean;
	isCompanyManaged: boolean;
}>(({ isVersionsFeatureEnabled, isCompanyManaged }) => {
	const [filters, { setFilters }] = useFilters();
	const [queryParams, setQueryParams] = useQueryParams(
		QUERY_PARAM_TEXT,
		QUERY_PARAM_SEARCH,
		QUERY_PARAM_ASSIGNEE,
		QUERY_PARAM_LABEL,
		QUERY_PARAM_ISSUE_PARENT,
		QUERY_PARAM_ISSUE_TYPE,
		QUERY_PARAM_VERSION,
		QUERY_PARAM_CUSTOM_FILTER,
		QUERY_PARAM_QUICK_FILTER,
		QUERY_PARAM_JQL,
	);

	const filtersFromQuery = useMemo(
		() => getFiltersFromQuery(queryParams, isVersionsFeatureEnabled, isCompanyManaged),
		[isVersionsFeatureEnabled, queryParams, isCompanyManaged],
	);
	const filtersRef = useRef(filtersFromQuery);

	const queryFromFilters = useMemo(
		() => getQueryFromFilters(filters, isCompanyManaged),
		[filters, isCompanyManaged],
	);

	// set as initially empty so we can sync the query parameters from the filters store
	const routeRef = useRef<{
		[QUERY_PARAM_TEXT]?: string;
		[QUERY_PARAM_SEARCH]?: string;
		[QUERY_PARAM_ASSIGNEE]?: string;
		[QUERY_PARAM_LABEL]?: string;
		[QUERY_PARAM_ISSUE_PARENT]?: string;
		[QUERY_PARAM_ISSUE_TYPE]?: string;
		[QUERY_PARAM_VERSION]?: string;
		[QUERY_PARAM_CUSTOM_FILTER]?: string;
		[QUERY_PARAM_QUICK_FILTER]?: string;
		[QUERY_PARAM_JQL]?: string;
	}>({
		[QUERY_PARAM_TEXT]: undefined,
		[QUERY_PARAM_SEARCH]: undefined,
		[QUERY_PARAM_ASSIGNEE]: undefined,
		[QUERY_PARAM_LABEL]: undefined,
		[QUERY_PARAM_ISSUE_PARENT]: undefined,
		[QUERY_PARAM_ISSUE_TYPE]: undefined,
		[QUERY_PARAM_VERSION]: undefined,
		[QUERY_PARAM_CUSTOM_FILTER]: undefined,
		[QUERY_PARAM_QUICK_FILTER]: undefined,
		[QUERY_PARAM_JQL]: undefined,
	});

	// set initial filters from URL (prev componentDidMount)
	useEffect(() => {
		// Sync state -> url
		if (!isEqual(queryFromFilters, routeRef.current)) {
			if (!isEqual(queryParams, queryFromFilters)) {
				setQueryParams(queryFromFilters);
			}
			routeRef.current = queryFromFilters;
		}
	}, [setQueryParams, queryFromFilters, queryParams]);

	useEffect(() => {
		// Sync url -> state
		if (!isEqual(filtersFromQuery, filtersRef.current)) {
			// check if filters state is already updated to prevent useless update
			if (!isEqual(filtersFromQuery, filters)) {
				setFilters(filtersFromQuery);
			}
			filtersRef.current = filtersFromQuery;
		}
	}, [filters, filtersFromQuery, setFilters]);

	return null;
});
